import React, { FunctionComponent, useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import cx from 'classnames';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectReportName, selectTouched, calculatorActions } from '../../../../../redux/calculatorSlice';
import { StringFormatter } from '../../../../../utils';
import { AppThunkDispatch } from '../../../../../interfaces/redux.interface';
import { CircularButton, Dialog } from '../../../../../components';
import { paths } from '../../../..';
import useStyles from './InterventionContainer.styles';

interface IProps {
  header?: React.ReactElement;
  body?: React.ReactElement;
  longTermBody?: React.ReactElement;
  footer?: React.ReactElement;
  longTermFooter?: React.ReactElement;
  disabled: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  calculations?: React.ReactElement; 
}

const InterventionContainer: FunctionComponent<IProps> = ({
  header,
  body,
  longTermBody,
  longTermFooter,
  footer,
  disabled,
  onSubmit,
  calculations
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppThunkDispatch = useDispatch();
  const [isSaveExitDialogOpen, setIsSaveExitDialogOpen] = useState(false);
  const reportName = useSelector(selectReportName);
  const touched = useSelector(selectTouched);

  const pathOptions = {
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsEmergencyDepartmentUse
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsAsthma
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsImmunizations
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsEmergencyDepartmentUse
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsAsthma
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsOralHealth
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsPostpartumMaternalDepression
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsPositiveParenting
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsPositiveParenting
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsEarlyDetection
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsEarlyDetection
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsDiapers
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsHealthyBirthSpacing
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsPostpartumMaternalDepression
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsIntimatePartnerViolence
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsHealthyBirthSpacing
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsBreastfeeding
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsIntimatePartnerViolence
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsSmokingCessation
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsBreastfeeding
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsDiapers
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsImmunizations
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.interventionsFoodSecurity
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsSmokingCessation
      ),
    },
  };

  const onExitCalculator = () => {
    if (touched) {
      setIsSaveExitDialogOpen(true);
    } else {
      navigate(paths.calculatorDashboard);
    }
  };

  const onSaveAndExit = () => {
    setIsSaveExitDialogOpen(false);
    dispatch(calculatorActions.saveReport({ update: true,navigate:navigate,
      pathname:location.pathname }))
      .then(() =>
        navigate(paths.calculatorDashboard)
      );
  };

  return (
    <>
      <form className={classes.container} onSubmit={onSubmit}>
        {header}
        <div className={cx({ [classes.disabled]: disabled })}>
          {body}
          {footer}
        </div>
        <div className={cx({ [classes.disabled]: disabled })}>
          {longTermBody}
          {longTermFooter}
        </div>
        <div className={cx({ [classes.disabled]: disabled })}>
          {calculations}
        </div>
        {disabled && (
          <div className={classes.toggleOffTextWrapper}>
            <Typography className={classes.toggleOffText}>
              This intervention is not provided at your site.<br/>Skip this step or toggle it on.
            </Typography>
          </div>
        )}
        <div className={classes.buttonWrapper}>
          <IconButton className={classes.homeBtn} onClick={onExitCalculator}>
            <ExitToApp />
            Exit
          </IconButton>
          {decodeURIComponent(location.pathname) !==
            StringFormatter.replaceWithReportName(
              reportName,
              paths.interventionsOralHealth
            ) && (
            <CircularButton
              type="button"
              color="primary"
              customClass={`${classes.button} ${classes.secondaryButton}`}
              onClick={() => {
                navigate(pathOptions[decodeURIComponent(location.pathname)].backTo);
              }}
            >
              Previous
            </CircularButton>
          )}
          <CircularButton
            type="submit"
            color="primary"
            variant="contained"
            customClass={classes.button}
          >
            {location.pathname !==
              StringFormatter.replaceWithReportName(
                reportName,
                paths.interventionsFoodSecurity
              ) &&
              disabled &&
              'Skip Step'}
            {location.pathname !==
              StringFormatter.replaceWithReportName(
                reportName,
                paths.interventionsFoodSecurity
              ) &&
              !disabled &&
              'Save & Next'}
            {location.pathname ===
              StringFormatter.replaceWithReportName(
                reportName,
                paths.interventionsFoodSecurity
              ) && 'Save & View Report'}
          </CircularButton>
        </div>
      </form>
      <Dialog
        id="save-and-exit_dialog.testid"
        title="Are you sure you want to leave this page?"
        open={isSaveExitDialogOpen}
        onClose={() => {
          setIsSaveExitDialogOpen(false);
        }}
        onSubmitBtnClick={onSaveAndExit}
        submitBtnText="Save & Exit"
        cancelBtnText="Exit without saving"
        onCancelBtnClick={() => navigate(paths.calculatorDashboard)}
      >
        <Typography >
          Any unsaved changes will be lost
        </Typography>
      </Dialog>
    </>
  );
};

export default InterventionContainer;
